import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CoverImage from './CoverImage';
import { trackEvent } from '../services/analytics';
import '../styles/NotFoundPage.css';

function NotFoundPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [popularSearches, setPopularSearches] = useState([]);

  useEffect(() => {
    trackEvent('not_found_page_viewed', { path: window.location.pathname });

    fetch('/api/search/meta?type=all&limit=6')
      .then((response) => (response.ok ? response.json() : { popular: [] }))
      .then((data) => setPopularSearches(Array.isArray(data.popular) ? data.popular : []))
      .catch(() => setPopularSearches([]));
  }, []);

  const runSearch = async (queryText) => {
    const trimmed = String(queryText || '').trim();
    if (!trimmed) {
      return;
    }

    setLoading(true);
    setSearched(true);
    trackEvent('not_found_search_submitted', { query: trimmed.slice(0, 120) });

    try {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmed, type: 'all', limit: 8, offset: 0 }),
      });
      const data = response.ok ? await response.json() : { books: [] };
      setResults(Array.isArray(data.books) ? data.books : []);
    } catch (error) {
      console.error('Not-found page search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(query);
  };

  const handleBookOpen = (book) => {
    navigate(`/book/${book.biblio_id}`, { state: { from: 'not-found-search' } });
  };

  return (
    <section className="not-found-page">
      <div className="not-found-page__hero">
        <span className="not-found-page__code" aria-hidden="true">404</span>
        <h1>We couldn't find that page</h1>
        <p>The link may be outdated, or the page may have moved. Try searching the catalog instead.</p>
      </div>

      <form className="not-found-page__search" onSubmit={handleSubmit}>
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for books, journals, articles..."
          autoFocus
        />
        <button type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
      </form>

      {!searched && popularSearches.length > 0 && (
        <div className="not-found-page__popular">
          <span>Popular searches:</span>
          {popularSearches.map((item) => (
            <button
              key={item.query}
              type="button"
              className="not-found-page__popular-pill"
              onClick={() => { setQuery(item.query); runSearch(item.query); }}
            >
              {item.query}
            </button>
          ))}
        </div>
      )}

      {searched && !loading && results.length === 0 && (
        <p className="not-found-page__empty">No results found. Try a different search term.</p>
      )}

      {results.length > 0 && (
        <div className="not-found-page__results">
          {results.map((book) => (
            <button
              key={book.biblio_id}
              type="button"
              className="not-found-page__result-card"
              onClick={() => handleBookOpen(book)}
            >
              <CoverImage
                biblioId={book.biblio_id}
                isbn={book.isbn}
                alt={book.title}
                className="not-found-page__result-cover"
                width={56}
                height={82}
                sizeCode="S"
              />
              <span className="not-found-page__result-info">
                <span className="not-found-page__result-title">{book.title}</span>
                {book.author && <span className="not-found-page__result-author">{book.author}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="not-found-page__actions">
        <button type="button" onClick={() => navigate('/')}>Go to Homepage</button>
        <button type="button" onClick={() => navigate('/subjects')}>Browse Subjects</button>
      </div>
    </section>
  );
}

export default NotFoundPage;
